import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { DocumentStore } from '@burfy/docs-core';
import { IndexedDbDocumentStore } from './indexeddb-document-store.ts';

/**
 * Supplies the document store to the tree.
 *
 * A context rather than a module-level singleton: tests inject an in-memory
 * store, and a future build can swap IndexedDB for an HTTP store without
 * touching a single component.
 */
const DocumentStoreContext = createContext<DocumentStore | null>(null);

export interface DocumentStoreProviderProps {
  /** Defaults to the IndexedDB implementation. */
  store?: DocumentStore;
  children: ReactNode;
}

export function DocumentStoreProvider({ store, children }: DocumentStoreProviderProps) {
  const value = useMemo(() => store ?? new IndexedDbDocumentStore(), [store]);

  return (
    <DocumentStoreContext.Provider value={value}>
      {children}
    </DocumentStoreContext.Provider>
  );
}

export function useDocumentStore(): DocumentStore {
  const store = useContext(DocumentStoreContext);
  if (!store) {
    throw new Error('useDocumentStore must be used inside a <DocumentStoreProvider>');
  }
  return store;
}
